import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Chip, ClearFiltersButton, SelectFilter } from "@outpave/ds";
import { TimeFrameEnum, TimeFrameTitles } from "enums/AnalyticsTimeFrame";
import { Inline } from "components/layout/reusableLayouts";
import { cardLabel } from "components/admin/cards/CardSearchAutoComplete";
import { fullName } from "utils/fullName";
import { useSelectFilterLabels } from "utils/selectFilterLabels";
import {
  AnalyticsFilters,
  DEFAULT_ANALYTICS_FILTERS,
  DEFAULT_TIME_FRAME,
  appliedFilterCount,
} from "./analyticsFilters";

interface AnalyticsFilterBarProps {
  filters: AnalyticsFilters;
  onChange: (next: AnalyticsFilters) => void;
  /** Opens the drawer that edits the filters shown here as chips. */
  onEditFilters: () => void;
}

interface AppliedChip {
  key: string;
  label: string;
  clear: Partial<AnalyticsFilters>;
}

const AnalyticsFilterBar: React.FC<AnalyticsFilterBarProps> = ({
  filters,
  onChange,
  onEditFilters,
}) => {
  const { t } = useTranslation();
  const selectLabels = useSelectFilterLabels();

  const timeFrameOptions = useMemo(
    () =>
      Object.entries(TimeFrameTitles).map(([value, title]) => ({
        value,
        label: t(title),
      })),
    [t],
  );

  // Names are runtime data, so only the filter's own name is translated.
  const candidates: (AppliedChip | null)[] = [
    filters.expenseCategory && {
      key: "category",
      label: `${t("Category")}: ${filters.expenseCategory.name}`,
      clear: { expenseCategory: null },
    },
    filters.employee && {
      key: "purchaser",
      label: `${t("Purchaser")}: ${fullName(filters.employee)}`,
      clear: { employee: null },
    },
    filters.card && {
      key: "card",
      label: `${t("Card")}: ${cardLabel(filters.card)}`,
      clear: { card: null },
    },
    filters.allocation && {
      key: "allocation",
      label: `${t("Allocation")}: ${filters.allocation.allocationCode}`,
      clear: { allocation: null, allocationType: null },
    },
  ];
  const applied = candidates.filter(
    (chip): chip is AppliedChip => chip !== null,
  );

  return (
    <Inline gap="var(--space-3)" align="center">
      <SelectFilter
        label={t("Timeframe")}
        options={timeFrameOptions}
        searchable={false}
        labels={selectLabels}
        selected={filters.timeFrame}
        onChange={(value) =>
          onChange({
            ...filters,
            timeFrame: (value as TimeFrameEnum | null) ?? DEFAULT_TIME_FRAME,
          })
        }
      />
      {applied.map((chip) => (
        <Chip
          key={chip.key}
          selected
          labels={{ remove: t("Remove") }}
          onClick={onEditFilters}
          onClear={() => onChange({ ...filters, ...chip.clear })}
        >
          {chip.label}
        </Chip>
      ))}
      <ClearFiltersButton
        count={appliedFilterCount(filters)}
        label={t("Clear Filters")}
        onClear={() => onChange(DEFAULT_ANALYTICS_FILTERS)}
      />
    </Inline>
  );
};

export default AnalyticsFilterBar;
